import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { useProject } from "@utils/useProject";
import { useConversation } from "@pages/Assistant/utils/useConversation";
import { useProgram } from "@pages/Program/utils/useProgram";
import { ProgramCardProps } from "@shared/interfaces/ProgramFiles";
import { useUser } from "@services/UserContext";

interface SharedAppData {
  projects: Project[];
  conversations: any[];
  programs: ProgramCardProps[];
  loading: boolean;
  loadingProjects: boolean;
  loadingConversations: boolean;
  loadingPrograms: boolean;
}

interface SharedData {
  projects: Project[];
  conversations: any[];
  programs: ProgramCardProps[];
  loading: boolean;
  loadingProjects: boolean;
  loadingConversations: boolean;
  loadingPrograms: boolean;

  refreshTrigger: number;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
  refreshChatsTrigger: number;
  setRefreshChatsTrigger: React.Dispatch<React.SetStateAction<number>>;
  refreshProgramsTrigger: number;
  setRefreshProgramsTrigger: React.Dispatch<React.SetStateAction<number>>;
  handleTriggerTasksRefresh: () => void;
  refreshTasksTrigger: number;
}

interface Project {
  id: number;
  name: string;
  icon: string;
}

export const AppContext = createContext<SharedData | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [sharedData, setSharedData] = useState<SharedAppData>({
    projects: [],
    conversations: [],
    programs: [],
    loading: false,
    loadingProjects: false,
    loadingConversations: false,
    loadingPrograms: false,
  });

  const { getProjectsDropdown } = useProject();
  const { fetchUserConversations } = useConversation();
  const { getProgramsDropdown } = useProgram();
  const { user } = useUser();

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [refreshChatsTrigger, setRefreshChatsTrigger] = useState(0);
  const [refreshProgramsTrigger, setRefreshProgramsTrigger] = useState(0);
  const [refreshTasksTrigger, setRefreshTasksTrigger] = useState(0);

  const handleTriggerTasksRefresh = () => {
    setRefreshTasksTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    const fetchProjects = async () => {
      setSharedData((prev) => ({ ...prev, loadingProjects: true }));
      try {
        const fetchedProjects = await getProjectsDropdown();
        setSharedData((prev) => ({
          ...prev,
          projects: fetchedProjects.sort((a: any, b: any) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
          ),
        }));
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      } finally {
        setSharedData((prev) => ({ ...prev, loadingProjects: false }));
      }
    };

    fetchProjects();
  }, [refreshTrigger, user]);

  useEffect(() => {
    const fetchConversations = async () => {
      setSharedData((prev) => ({ ...prev, loadingConversations: true }));
      try {
        const conversations = await fetchUserConversations();
        setSharedData((prev) => ({ ...prev, conversations }));
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
      } finally {
        setSharedData((prev) => ({ ...prev, loadingConversations: false }));
      }
    };

    fetchConversations();
  }, [refreshChatsTrigger, user]);

  useEffect(() => {
    const fetchPrograms = async () => {
      setSharedData((prev) => ({ ...prev, loadingPrograms: true }));
      try {
        const programs = await getProgramsDropdown();
        setSharedData((prev) => ({ ...prev, programs }));
      } catch (error) {
        console.error("Failed to fetch programs:", error);
      } finally {
        setSharedData((prev) => ({ ...prev, loadingPrograms: false }));
      }
    };

    fetchPrograms();
  }, [refreshProgramsTrigger, user]);

  useEffect(() => {
    setSharedData((prev) => ({
      ...prev,
      loading:
        prev.loadingProjects &&
        prev.loadingConversations &&
        prev.loadingPrograms,
    }));
  }, [
    sharedData.loadingProjects,
    sharedData.loadingConversations,
    sharedData.loadingPrograms,
  ]);

  return (
    <AppContext.Provider
      value={{
        ...sharedData,
        refreshTrigger,
        refreshChatsTrigger,
        refreshProgramsTrigger,
        setRefreshChatsTrigger,
        setRefreshProgramsTrigger,
        setRefreshTrigger,
        handleTriggerTasksRefresh,
        refreshTasksTrigger,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the AppContext
export const useAppContext = (): SharedData => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};
